/**
 * Общие элементы экранов разделов. Числа и подписи — готовые строки сервера ([F-4.0.11]);
 * незнание — прочерк и причина словами, не ноль ([F-4.0.12]).
 */
import { useState } from 'react'
import type { Known, Proposal, Tone, Verdict } from '../api/sections.js'
import { Icon, StatusTag, type IconName } from '../components/icons/Icon.js'
import { DASH } from './common.js'

/** Надпись секции капсом и справа — уточнение (период, счётчик). */
export function Sect({ title, aside, id }: { title: string; aside?: string | null; id?: string }): React.JSX.Element {
  return (
    <h2 className="sect wb-overline" id={id}>
      <span>{title}</span>
      {aside != null && aside !== '' && <span className="sect-aside">{aside}</span>}
    </h2>
  )
}

/** Значение или прочерк; у прочерка диктор слышит «нет данных». */
export function Num({ v }: { v: Known | undefined }): React.JSX.Element {
  if (v?.display != null) return <>{v.display}</>
  return <><span aria-hidden="true">{DASH}</span><span className="vh">нет данных</span></>
}

/** bare — строка-пояснение без значения справа («Когда остановиться» + текст ниже). */
export interface KvRow { label: string; v?: Known; text?: string | null; sub?: string | null; strong?: boolean; bare?: boolean }

/** Пары «подпись — значение». Причина незнания — строкой под парой, а не всплывающей подсказкой. */
export function Kv({ rows, testid }: { rows: ReadonlyArray<KvRow | null | false>; testid?: string }): React.JSX.Element {
  return (
    <dl className="kvl" data-testid={testid}>
      {rows.filter((r): r is KvRow => r !== null && r !== false).map((r, k) => {
        const reason = r.v !== undefined && r.v.display == null ? r.v.reason : null
        const sub = r.sub ?? reason
        return (
          <div className="kvl-row" key={k}>
            <dt>{r.label}</dt>
            {r.bare === true ? <dd aria-hidden="true" /> : <dd className={r.strong ? 'is-strong' : undefined}>{r.text !== undefined ? (r.text ?? <Num v={undefined} />) : <Num v={r.v} />}</dd>}
            {sub != null && sub !== '' && <p className="kvl-sub">{sub}</p>}
          </div>
        )
      })}
    </dl>
  )
}

/** Метка статуса: смысловой тон — иконка формы + слово + цвет; нейтральный — серая плашка без иконки. */
export function ToneTag({ tone, word }: { tone: Tone; word: string }): React.JSX.Element {
  if (tone === 'neutral') return <span className="tag tag-neutral">{word}</span>
  return <StatusTag tone={tone} word={word} />
}

/** Строка-переход: иконка, название, подпись, шеврон. */
export function LinkRow({ icon, title, sub, onClick, testid }: {
  icon: IconName; title: string; sub?: string | null; onClick: () => void; testid: string
}): React.JSX.Element {
  return (
    <button type="button" className="row row-tap has-ic" data-testid={testid} onClick={onClick}>
      <Icon name={icon} className="ic-muted" />
      <span className="row-main">
        <span className="row-title">{title}</span>
        {sub != null && sub !== '' && <span className="row-meta">{sub}</span>}
      </span>
      <Icon name="nav-next" className="ic-chev row-end" />
    </button>
  )
}

/** Раскрывающийся пункт на нативном details: клавиатура и диктор работают сами. */
export function Fold({ q, children, testid, open }: { q: React.ReactNode; children: React.ReactNode; testid?: string; open?: boolean }): React.JSX.Element {
  return (
    <details className="fold" data-testid={testid} open={open}>
      <summary className="fold-q"><span>{q}</span><Icon name="nav-next" className="ic-chev fold-chev" /></summary>
      <div className="fold-a">{children}</div>
    </details>
  )
}

/**
 * «Что предлагает агент» — действия из последнего утреннего отчёта. Штаб кабинет не меняет:
 * действие делается в кабинете WB, и экран говорит это прямо, без обещаний.
 */
export function Proposals({ items, onReport, testid = 'proposals' }: {
  items: ReadonlyArray<Proposal>; onReport?: (id: string) => void; testid?: string
}): React.JSX.Element | null {
  if (items.length === 0) return null
  return (
    <>
      <Sect title="Что предлагает агент" aside="из утреннего отчёта" />
      <section className="card" data-testid={testid}>
        {items.map((p, k) => (
          <div className="step" key={k}>
            <span className="step-n" aria-hidden="true">{k + 1}</span>
            <div className="step-main">
              <p className="step-title">{p.title}</p>
              {p.expected_effect != null && <p className="step-note">Что даст: {lowerFirst(p.expected_effect)}</p>}
            </div>
          </div>
        ))}
        <p className="card-foot">Сделать это можно в кабинете WB: Штаб читает данные кабинета и сам ничего в нём не меняет.</p>
        {onReport !== undefined && items[0] !== undefined && (
          <button type="button" className="row row-tap row-link has-ic" data-testid={testid + '-report'} onClick={() => onReport(items[0]!.report_id)}>
            <Icon name="section-reports" className="ic-action" />
            <span className="row-main"><span className="row-title">Открыть отчёт</span></span>
            <Icon name="nav-next" className="ic-chev row-end" />
          </button>
        )}
      </section>
    </>
  )
}

/** «Что даст: часть штрафов…» — после двоеточия со строчной; аббревиатуры («ДРР…») не трогаем. */
export function lowerFirst(s: string): string {
  const a = s.charAt(0), b = s.charAt(1)
  return b !== '' && b === b.toLowerCase() && b !== b.toUpperCase() ? a.toLowerCase() + s.slice(1) : s
}

/**
 * «Полезно / Мимо» — оценка отчёта или предложения агента. Повторное нажатие снимает оценку.
 * Кнопки бота этого не делают ([19.2]); оценка видна в панели обслуживания и подстраивает агентов.
 */
export function VerdictRow({ value, onSet, testid, q = 'Это было полезно?' }: {
  value: Verdict; onSet: (v: Verdict) => Promise<boolean>; testid: string; q?: string
}): React.JSX.Element {
  const [v, setV] = useState<Verdict>(value)
  const [busy, setBusy] = useState(false)
  const pick = (x: 'useful' | 'miss'): void => {
    const next = v === x ? null : x
    setBusy(true)
    void onSet(next).then((ok) => { setBusy(false); if (ok) setV(next) })
  }
  const b = (x: 'useful' | 'miss', label: string): React.JSX.Element => (
    <button type="button" className={'seg' + (v === x ? ' seg-on' : '')} aria-pressed={v === x} disabled={busy}
      data-testid={`${testid}-${x}`} onClick={() => pick(x)}>{label}</button>
  )
  return (
    <div className="verdict" role="group" aria-label={q} data-testid={testid}>
      <span className="row-meta">{q}</span>
      <span className="segs">{b('useful', 'Полезно')}{b('miss', 'Мимо')}</span>
    </div>
  )
}
